// Inbox Events Hook - live notification badge over SSE
//
// Subscribes to /api/rest/inbox-events (own recipient channel only, R6) and invalidates
// Inbox counts/list and approval details; audible events also play a short tone.
//
// Deliberately does NOT close the EventSource on `onerror` (precedent `useQboSyncEvents.ts:93-98`
// closes there and defeats the browser's native reconnect — hot spot "Hook never reconnects"): the
// stream only closes on unmount or when the signed-in user/org changes.
//
// AGENTS: Keep documentation up to date when changing this module

import { useAuth } from "@clerk/nextjs";
import { useEffect } from "react";

import { inboxEventSchema } from "~/shared/notifications/inbox-event";
import { api } from "~/utils/api";

/**
 * No audio asset in the repo (U-6) — a short tone generated with the Web Audio API. Every failure
 * mode (no `AudioContext`, a synchronous throw, or a blocked-autoplay rejection) is caught here so
 * the badge update above it never depends on the sound succeeding.
 */
const playInboxChime = (): void => {
  try {
    // D01a/D01b: `webkitAudioContext` is Safari's pre-standard name for `AudioContext`, absent from
    // `lib.dom.d.ts`'s `Window` type — there is no typed way to read it, so this narrows through
    // `unknown` on purpose (never a shortcut around a type the compiler could otherwise infer).
    // Every failure mode (no constructor at all, or the read throwing) is caught below, so a wrong
    // read here degrades to "no sound", never a runtime crash. The fallback branch is exercised by
    // `useInboxEvents.test.ts` ("falls back to webkitAudioContext when AudioContext is absent" and
    // "does nothing when neither AudioContext nor webkitAudioContext exists").
    const AudioContextCtor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }
    const context = new AudioContextCtor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.15, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.3);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.3);
    // Release the context once the tone has finished. A browser holds each context's audio resources
    // until `close()` and caps how many one page may open, so a tab left open all day through many
    // notifications would eventually have `new AudioContextCtor()` above throw into the catch below —
    // and the chime would stop with nothing else to show for it. `onended` is the first moment the
    // context is certainly no longer needed; `close()` rejects only on an already-closed context.
    oscillator.onended = () => {
      void context.close().catch(() => undefined);
    };
    const resumeResult: unknown = context.resume();
    if (resumeResult instanceof Promise) {
      resumeResult.catch(() => {
        // Browser blocks audio until the user interacts with the page — badge still updates.
      });
    }
  } catch {
    // No Web Audio API support, or a synchronous throw — badge still updates.
  }
};

export const useInboxEvents = (): void => {
  const { userId, orgId } = useAuth();
  const utils = api.useContext();

  useEffect(() => {
    if (!userId || !orgId) {
      return;
    }

    const eventSource = new EventSource("/api/rest/inbox-events");

    eventSource.onmessage = event => {
      if (!event.data) {
        return;
      }
      let rawPayload: unknown;
      try {
        rawPayload = JSON.parse(event.data);
      } catch {
        return;
      }
      const parsed = inboxEventSchema.safeParse(rawPayload);
      if (!parsed.success) {
        return;
      }
      // Round 23 UAT fix: `inbox.list` is invalidated here too, not just `counts`. Invalidating only
      // the count meant a notification arriving while the Inbox panel was open bumped the badge while
      // the rows below it stayed exactly as they were — the badge said 6, the list showed 5, and
      // nothing the user did in the panel reconciled them.
      void utils.inbox.counts.invalidate();
      void utils.inbox.list.invalidate();
      // Show completed keeps the same Task/card mounted after another approver decides.
      void utils.orders.approvals.detail.invalidate();
      if (parsed.data.playSound) {
        playInboxChime();
      }
    };

    // Deliberately does nothing: see the module comment above (no close, native reconnect handles it).
    eventSource.onerror = () => undefined;

    return () => {
      eventSource.close();
    };
  }, [userId, orgId, utils]);
};
